// js/components/ProductSection.js
import Delegate from 'ftdomdelegate';
import { ProductGallery } from './product-gallery.js';
import { ShippingEstimator } from './shipping-estimator.js';
import { QuantityPicker } from './quantity-picker.js';
import { Listeners, SecureStorage } from '../utils.js';

export class ProductSection extends HTMLElement {
  constructor() {
    super();
    this.delegateElement = new Delegate(this);
    const settingsAttr =
      this.getAttribute("data-section-settings") || this.getAttribute("data-settings") || "{}";

    try {
      this.options = JSON.parse(settingsAttr) || {};
    } catch (error) {
      console.error("Invalid product section settings JSON:", error);
      this.options = {};
    }
  }
  connectedCallback() {
    this.productGallery = new ProductGallery(this, this.options);
    if (this.options["isQuickView"] && window.Shopify.PaymentButton) {
      Shopify.PaymentButton.init();
    }
    if (this.options["showShippingEstimator"]) {
      this.shippingEstimator = new ShippingEstimator(this);
    }
    
    // Save recently viewed product
    this._saveRecentlyViewedProduct();
    
    this._attachListeners();
  }
  disconnectedCallback() {
    this.delegateElement.off();
    if (this.productGallery) {
      this.productGallery.destroy();
    }
    if (this.shippingEstimator) {
      this.shippingEstimator.destroy();
    }
    
    // Cleanup sticky form observer
    if (this._stickyObserver) {
      this._stickyObserver.disconnect();
    }
  }
  onUnload() {
    this.disconnectedCallback();
  }
  _attachListeners() {
    this._onVariantChangedListener = this._onVariantChanged.bind(this);
    this.addEventListener("variant:change", this._onVariantChangedListener);
    
    // Sticky form functionality with Intersection Observer
    this._initStickyFormObserver();
  }
  /**
   * This method is called when the variant is changed due to option
   */
  _onVariantChanged(event) {
    this.productGallery.variantHasChanged(event.detail.variant);
  }

  _initStickyFormObserver() {
    const stickyWrapper = document.querySelector('.sticky__product-info-wrapper');
    const buyButton = document.querySelector('.product-form__add-button');
    
    if (!stickyWrapper || !buyButton) return;
    
    // Get the initial position of the buy button
    const buyButtonRect = buyButton.getBoundingClientRect();
    const initialButtonTop = buyButtonRect.top + window.scrollY;
    
    // Create intersection observer
    this._stickyObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Buy button is visible - hide sticky form
          stickyWrapper.classList.remove('is-visible');
        } else {
          // Buy button is not visible - check if user has scrolled past initial position
          const currentScrollY = window.scrollY;
          if (currentScrollY > initialButtonTop) {
            // User has scrolled past the initial button position - show sticky form
            stickyWrapper.classList.add('is-visible');
          } else {
            // User is at the top of the page - hide sticky form
            stickyWrapper.classList.remove('is-visible');
          }
        }
      });
    }, {
      root: null, // Use viewport as root
      rootMargin: '0px', // No margin
      threshold: 0.1 // Trigger when 10% of the button is visible
    });
    
    // Start observing the buy button
    this._stickyObserver.observe(buyButton);
  }

  /**
   * Save the current product to recently viewed products
   */
  _saveRecentlyViewedProduct() {
    const productId = window.theme?.productId;
    if (!productId) return;

    // Get existing items from storage
    let items = SecureStorage.getJSON('recentlyViewedProducts', []);

    // If the product already exists, remove it first
    if (items.includes(productId)) {
      items.splice(items.indexOf(productId), 1);
    }

    // Add current product to the beginning
    items.unshift(productId);

    // Save back to storage, keeping only the 18 most recent
    SecureStorage.setJSON('recentlyViewedProducts', items.slice(0, 18));
  }
}

class CosPhoneField extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.Listeners = new Listeners();
    this.select = this.querySelector('[data-prefix-select]');
    this.label = this.querySelector('[data-prefix-label]');
    this.labelText = this.querySelector('[data-prefix-label-text]');
    this.phone = this.querySelector('input[type="tel"]');
    
    // Event listeners
    this.Listeners.add(this.select, 'change', () => this.validate());
    this.Listeners.add(this.phone, 'change', () => this.validatePhone());
  }

  disconnectedCallback() {
    if (this.Listeners) {
      this.Listeners.removeAll();
    }
  }

  validatePhone() {
    // Validează și corectează numărul de telefon
    let phone = this.phone.value;
    
    // Caută prefixul în lista de opțiuni
    for (let i = 0; i < this.select.options.length; i++) {
      if (phone.startsWith(this.select.options.item(i).value)) {
        this.select.selectedIndex = i;
      }
    }
    

    this.labelText.innerText = this.select.options[this.select.selectedIndex].dataset.countryShortName;
    return phone;
  }

  correctPhone() {
    // Corectează formatul numărului de telefon
    let oldPrefix = this.phone.getAttribute('data-prefix');
    const prefix = this.select.value;
    let phone = this.phone.value.trim();
    
    // Adaugă prefixul dacă nu îl are
    if (!phone.startsWith(prefix, 0)) {
      // phone = prefix + " " + phone;
      phone = prefix + " " + phone;
    }

    // Scoate prefixul dacă îl are
    // if (phone.startsWith(prefix, 0)) {
    //   phone = phone.replace(prefix, '');
    // }
    
    this.phone.setAttribute('data-prefix', prefix);
    return phone;
  }

  validate() {
    this.phone.value = this.correctPhone();
    this.labelText.innerText = this.select.options[this.select.selectedIndex].dataset.countryShortName;
    return this.phone.value;
  }
}

class CosOneClickOrderForm extends HTMLElement {
  constructor() {
    super();
    this.listeners = new Listeners();
    this.settings = {};
  }

  connectedCallback() {
    this._loadSettings();
    this._attachListeners();
  }

  _loadSettings() {
    try {
      const settingsData = this.getAttribute('data-settings');
      if (settingsData) {
        this.settings = JSON.parse(settingsData);
      }
    } catch (error) {
      console.error('Error parsing one-click order form settings:', error);
      this.settings = {};
    }
  }

  disconnectedCallback() {
    if (this.listeners) {
      this.listeners.removeAll();
    }
  }

  _attachListeners() {
    const form = this.querySelector('form');
    if (form) {
      this.listeners.add(form, 'submit', (event) => this._handleSubmit(event));
    }
  }

  _handleSubmit(event) {
    event.preventDefault();
    
    const form = event.target;
    
    if (!this._checkForm(form)) {
      return;
    }

    const formData = new FormData(form);
    const name = formData.get('name');
    const phone = formData.get('phone');
    
    this._submitOrder(name, phone);
  }

  _checkForm(form) {
    // Setează câmpurile ca obligatorii
    const nameField = form.querySelector('input[name="name"]');
    const phoneField = form.querySelector('input[type="tel"]');
    
    if (nameField) nameField.required = true;
    if (phoneField) phoneField.required = true;
    
    // Detectează și pregătește numărul de telefon cu prefix
    const phoneFieldComponent = form.querySelector('cos-phone-field');
    if (phoneFieldComponent && phoneField) {
      // Pregătește numărul cu prefix dacă nu îl are
      const phoneValue = phoneField.value.trim();
      if (phoneValue && !phoneValue.startsWith('+')) {
        // Obține prefixul selectat din cos-phone-field
        const prefixSelect = phoneFieldComponent.querySelector('[data-prefix-select]');
        if (prefixSelect) {
          const selectedPrefix = prefixSelect.value;
          phoneField.value = selectedPrefix + ' ' + phoneValue;
        }
      }
    }
    
    // Validează formularul
    if (!form.checkValidity()) {
      const invalidFields = form.querySelectorAll(":invalid");
      if (invalidFields.length > 0) {
        // Adaugă feedback vizual pentru câmpurile invalide
        this._showFieldError(invalidFields[0]);
        invalidFields[0].focus();
      }
      return false;
    }
    
    // Elimină erorile de câmp dacă formularul este valid
    this._clearFieldErrors();
    return true;
  }

  _showError(message) {
    // Șterge erorile existente
    this._clearMessages();

    // Folosește mesajul din setări sau cel implicit
    const errorMessage = this.settings.translations?.order_error || message;

    // Adaugă clasa has-error pentru styling
    this.classList.add('has-error');

    // Creează mesajul de eroare cu styling similar cu cos-code-reducer
    const errorDiv = document.createElement('div');
    errorDiv.className = 'one-click-order-message text-error';
    errorDiv.textContent = errorMessage;

    // Adaugă mesajul după formular
    const form = this.querySelector('form');
    form.appendChild(errorDiv);

    // Elimină mesajul după 5 secunde
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove();
      }
      // Elimină clasa has-error când mesajul dispare
      this.classList.remove('has-error');
    }, 5000);
  }

  _submitOrder(name, phone) {
    // Obține variant_id din formular
    const form = this.querySelector('form');
    const variantIdInput = form.querySelector('input[name="variant_id"]');
    
    if (!variantIdInput || !variantIdInput.value) {
      this._showError(this.settings.translations?.product_not_found || 'Nu s-a putut identifica produsul. Vă rugăm să reîncercați.');
      return;
    }
    
    const variantId = variantIdInput.value;

    // Afișează loading state
    this._showLoading();

    const apiEndpoint = this.settings.api_endpoint || '/apps/ff-app/api/one-click/create';
    
    fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: name,
        phone: phone,
        variant_id: variantId
      })
    })
    .then(res => {
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      return res.json();
    })
    .then(data => {
      this._showSuccess(this.settings.translations?.order_success || 'Comanda a fost plasată cu succes!');
      
      // Închide modalul după timeout-ul specificat
      const timeout = this.settings.timeout || 2000;
      setTimeout(() => {
        const modal = this.closest('.modal');
        if (modal) {
          modal.setAttribute('aria-hidden', 'true');
          document.documentElement.classList.remove('is-locked');
        }
      }, timeout);
    })
    .catch(err => {
      console.error('Error creating order:', err);
      this._showError(this.settings.translations?.order_error || 'A apărut o eroare la plasarea comenzii. Vă rugăm să reîncercați.');
    });
  }


  _clearMessages() {
    // Șterge toate mesajele existente
    const existingMessages = this.querySelectorAll('.one-click-order-message, .form__error, .form__success, .form__loading');
    existingMessages.forEach(message => message.remove());
    
    // Elimină clasa has-error
    this.classList.remove('has-error');
  }

  _showFieldError(field) {
    // Elimină erorile anterioare pentru acest câmp
    this._clearFieldErrors();
    
    // Adaugă clasa de eroare la câmp
    field.classList.add('field-error');
    
    // Adaugă clasa has-error la componenta părinte
    const fieldWrapper = field.closest('.form__input-wrapper, .one-click-order-modal__field');
    if (fieldWrapper) {
      fieldWrapper.classList.add('has-error');
    }
    
    // Adaugă iconița de eroare dacă nu există
    if (!fieldWrapper?.querySelector('.input-error-icon')) {
      const errorIcon = document.createElement('div');
      errorIcon.className = 'input-error-icon';
      errorIcon.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 1C4.134 1 1 4.134 1 8s3.134 7 7 7 7-3.134 7-7-3.134-7-7-7zm0 10.5c-.414 0-.75-.336-.75-.75s.336-.75.75-.75.75.336.75.75-.336.75-.75.75zm0-1.5c-.414 0-.75-.336-.75-.75V5.5c0-.414.336-.75.75-.75s.75.336.75.75v4.25c0 .414-.336.75-.75.75z" fill="currentColor"/></svg>';
      fieldWrapper?.appendChild(errorIcon);
    }
  }

  _clearFieldErrors() {
    // Elimină clasele de eroare de pe toate câmpurile
    const errorFields = this.querySelectorAll('.field-error');
    errorFields.forEach(field => field.classList.remove('field-error'));
    
    // Elimină clasele de eroare de pe wrapper-ele
    const errorWrappers = this.querySelectorAll('.has-error');
    errorWrappers.forEach(wrapper => wrapper.classList.remove('has-error'));
    
    // Elimină iconițele de eroare
    const errorIcons = this.querySelectorAll('.input-error-icon');
    errorIcons.forEach(icon => icon.remove());
  }

  _showLoading() {
    // Șterge mesajele existente
    this._clearMessages();

    // Folosește mesajul din setări sau cel implicit
    const loadingMessage = this.settings.translations?.processing_order || 'Se procesează comanda...';

    // Creează mesajul de loading cu styling similar cu cos-code-reducer
    const loadingDiv = document.createElement('div');
    loadingDiv.className = 'one-click-order-message text-loading';
    loadingDiv.textContent = loadingMessage;

    // Adaugă mesajul după formular
    const form = this.querySelector('form');
    form.appendChild(loadingDiv);
  }

  _showSuccess(message) {
    // Șterge mesajele existente
    this._clearMessages();

    // Folosește mesajul din setări sau cel implicit
    const successMessage = this.settings.translations?.order_success || message;

    // Creează mesajul de succes cu styling similar cu cos-code-reducer
    const successDiv = document.createElement('div');
    successDiv.className = 'one-click-order-message text-success';
    successDiv.textContent = successMessage;

    // Adaugă mesajul după formular
    const form = this.querySelector('form');
    form.appendChild(successDiv);

    // Elimină mesajul după 5 secunde
    setTimeout(() => {
      if (successDiv.parentNode) {
        successDiv.remove();
      }
    }, 5000);
  }
}


if (!window.customElements.get("cos-product")) window.customElements.define("cos-product", ProductSection);
if (!window.customElements.get("quantity-picker")) window.customElements.define("quantity-picker", QuantityPicker);
if (!window.customElements.get("cos-one-click-order-form")) window.customElements.define("cos-one-click-order-form", CosOneClickOrderForm);
if (!window.customElements.get("cos-phone-field")) window.customElements.define("cos-phone-field", CosPhoneField);
